import React, { useState, useEffect } from 'react';
import { API } from '../api';
import { formatCurrency } from '../api/currencyApi';
import { formatDate } from '../utils/dateUtils';
import AddHoursModal from './AddHoursModal';
import Pagination from './Pagination';
import './UserDetailPage.css';

const UserDetailPage = ({ userId, onBack, currency }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddHoursModal, setShowAddHoursModal] = useState(false);

  // Pagination state
  const [summaries, setSummaries] = useState([]);
  const [summariesLoading, setSummariesLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalSummaries, setTotalSummaries] = useState(0);
  const itemsPerPage = 5;

  useEffect(() => {
    const loadUser = async () => {
      setLoading(true);
      const res = await API.users.getById(userId);
      if (res.success) {
        setUser(res.data);
      }
      setLoading(false);
    };

    loadUser();
  }, [userId]);

  useEffect(() => {
    const fetchSummaries = async () => {
      setSummariesLoading(true);
      try {
        const res = await API.users.getUserSummaries(userId, currentPage, itemsPerPage);
        if (res.success) {
          const data = res.data;
          let items = [];
          let total = 0;

          if (data.data && Array.isArray(data.data)) {
             items = data.data;
             total = data.pagination?.total || 0;
          } else if (Array.isArray(data)) {
             items = data;
             total = data.length;
          }
          
          setSummaries(items.map(s => {
            let reaction = s.userReaction || s.reaction || 'none';
            if (s.reactions) {
              if (s.reactions.like > 0) reaction = 'like';
              else if (s.reactions.dislike > 0) reaction = 'dislike';
            }

            let viewsTotal = 0;
            let viewsToday = 0;

            if (s.views && typeof s.views === 'object') {
              viewsTotal = s.views.total || 0;
              viewsToday = s.views.today || 0;
            } else {
              viewsTotal = s.views || 0;
            }

            return {
              id: s.id || s.uuid,
              uuid: s.uuid || s.id,
              title: s.title || s.name || 'Untitled',
              date: s.date || s.createdAt || s.created_at || new Date().toISOString(),
              userReaction: reaction,
              views: viewsTotal,
              viewsToday: viewsToday,
              platform: s.platform || 'bot'
            };
          }));
          setTotalSummaries(total);
        }
      } catch (error) {
        console.error('Error fetching summaries:', error);
      } finally {
        setSummariesLoading(false);
      }
    };

    fetchSummaries();
  }, [userId, currentPage]);

  const handleAddHours = async (hours, reason) => {
    const res = await API.users.addHours(userId, hours, reason);
    if (res.success) {
      // Update local user data
      setUser({
        ...user,
        hours: res.data.newTotal
      });
      setShowAddHoursModal(false);
    }
  };

  if (loading) {
    return (
      <div className="user-detail-page">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Загрузка...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="user-detail-page">
        <button className="back-button" onClick={onBack}>
          ← Вернуться
        </button>
        <div className="empty-state">
          <p>Пользователь не найден</p>
        </div>
      </div>
    );
  }

  const totalSpent = (user.payments || []).reduce((sum, p) => sum + p.amount, 0);
  // const totalSummaries = user.summaries ? user.summaries.length : 0; // Removed, using state

  return (
    <div className="user-detail-page">
      <button className="back-button" onClick={onBack}>
        ← Вернуться к пользователям
      </button>

      {/* User Header */}
      <div className="user-header">
        {user.avatarUrl ? (
          <img 
            src={user.avatarUrl} 
            alt={user.firstName} 
            className="user-avatar-img"
          />
        ) : (
          <div className="user-avatar">{user.firstName.charAt(0)}</div>
        )}
        <div className="user-info">
          <h1>{user.firstName}</h1>
          {user.username ? (
            <a 
              href={`https://t.me/${user.username}`}
              target="_blank"
              rel="noopener noreferrer"
              className="username-link"
              style={{ display: 'block', color: '#0088cc', textDecoration: 'none', marginBottom: '4px' }}
            >
              @{user.username}
            </a>
          ) : (
            <p className="username">No username</p>
          )}
          <p className="user-id">ID: {user.id}</p>
          <p className="join-date">Присоединился: {formatDate(user.joinDate)}</p>
          
          {user.utm && (
            <div className="utm-tags">
              {['source', 'medium', 'campaign', 'content'].map(key => (
                user.utm[key] && (
                  <span key={key} className="utm-tag">
                    <span className="utm-key">{key}:</span> {user.utm[key]}
                  </span>
                )
              ))}
            </div>
          )}
        </div>
        {/* <button className="btn btn-primary" onClick={() => setShowAddHoursModal(true)}>
          + Добавить часы
        </button> */}
      </div>

      {/* Stats Grid */}
      <div className="user-stats-grid">
        {/* <div className="stat-card">
          <div className="stat-label">Доступные часы</div>
          <div className="stat-value">{user.hours.toFixed(1)} ч</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Потрачено средств</div>
          <div className="stat-value">{formatCurrency(totalSpent, currency)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Платежей</div>
          <div className="stat-value">{user.payments.length}</div>
        </div> */}
        <div className="stat-card">
          <div className="stat-label">Саммари</div>
          <div className="stat-value">{totalSummaries}</div>
        </div>
      </div>

      {/* Payment History */}
      {/* <div className="section">
        <h2>История платежей</h2>
        {user.payments.length === 0 ? (
          <div className="empty-message">Нет платежей</div>
        ) : (
          <div className="payments-list">
            {user.payments.map(payment => (
              <div key={payment.id} className="payment-item">
                <div className="payment-date">
                  {new Date(payment.date).toLocaleDateString('ru-RU')}
                </div>
                <div className="payment-status">✓ Успешно</div>
                <div className="payment-amount">
                  {formatCurrency(payment.amount, currency)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div> */}

      {/* Summaries */}
      <div className="section">
        <h2>Сгенерированные саммари ({totalSummaries})</h2>
        {summariesLoading ? (
           <div className="loading-state" style={{ padding: '20px', textAlign: 'center' }}>
             <div className="spinner"></div>
             <p>Загрузка саммари...</p>
           </div>
        ) : (!summaries || summaries.length === 0 ? (
          <div className="empty-message">Нет саммари</div>
        ) : (
          <>
            <div className="summaries-list">
              {summaries.map(summary => (
                <div key={summary.id} className="summary-item">
                  <div className="summary-info">
                    <div className="summary-title">{summary.title}</div>
                    <div className="summary-meta">
                      <span style={{ 
                        marginRight: '8px', 
                        padding: '2px 6px', 
                        borderRadius: '4px', 
                        backgroundColor: summary.platform === 'bot' ? '#e0f2fe' : '#f3f4f6', 
                        color: summary.platform === 'bot' ? '#0284c7' : '#4b5563',
                        fontSize: '11px',
                        fontWeight: '500'
                      }}>
                        {summary.platform === 'bot' ? 'Bot' : summary.platform}
                      </span>
                      {formatDate(summary.date)}
                    </div>
                  </div>
                  <div className="summary-ratings">
                    {summary.userReaction === 'like' && (
                      <span className="rating like">
                        Лайк
                      </span>
                    )}
                    {summary.userReaction === 'dislike' && (
                      <span className="rating dislike">
                        Дизлайк
                      </span>
                    )}
                    {summary.userReaction === 'none' && (
                      <span className="rating neutral">
                        — Нет оценки
                      </span>
                    )}
                    <span className="views">
                      👁️ {summary.views}
                      {summary.viewsToday > 0 && (
                        <span style={{ fontSize: '0.85em', marginLeft: '4px', color: '#16a34a' }} title="Просмотры за сегодня">
                          (+{summary.viewsToday})
                        </span>
                      )}
                    </span>
                  </div>
                  <a href={`${process.env.REACT_APP_SUMMARY_BASE_URL || 'https://earmiss.ru/#/summaries/'}${summary.uuid || summary.id}`} target="_blank" rel="noopener noreferrer" className="summary-link">
                    Смотреть
                  </a>
                </div>
              ))}
            </div>
            <Pagination 
              currentPage={currentPage}
              totalPages={Math.ceil(totalSummaries / itemsPerPage)}
              onPageChange={setCurrentPage}
            />
          </>
        ))}
      </div>

      {/* Add Hours Modal */}
      {showAddHoursModal && (
        <AddHoursModal
          user={user}
          onConfirm={handleAddHours}
          onClose={() => setShowAddHoursModal(false)}
        />
      )}
    </div>
  );
};

export default UserDetailPage;
